<template>
  <div class="effect-recommendations flex h-full min-h-0 flex-col">
    <div v-if="pending" class="columns-2 gap-3 lg:columns-3">
      <div
        v-for="idx in 12"
        :key="`effect-recommendation-skeleton-${idx}`"
        class="mb-3 inline-block w-full overflow-hidden rounded-xl shimmer"
        :style="{ aspectRatio: idx % 3 === 0 ? '0.75' : '0.68' }"
      />
    </div>

    <div
      v-else-if="recommendations.length === 0"
      class="flex h-full min-h-60 flex-col items-center justify-center text-center text-white/45"
    >
      <UIcon name="i-lucide-sparkles" class="mb-3 size-10" />
      <p class="text-sm">No effects available</p>
    </div>

    <template v-else>
      <div class="columns-2 gap-3 [column-fill:balance] lg:columns-3">
        <NuxtLink
          v-for="effect in recommendations"
          :key="`${effect.effect_type}-${effect.code || effect.id}`"
          :to="effectHref(effect)"
          class="effect-recommendations__card group/card mb-3 inline-block w-full overflow-hidden rounded-xl bg-white/5 text-inherit no-underline ring-1 ring-white/10 transition hover:bg-white/10 hover:ring-white/20"
          :style="effectCardAspectStyle(effect)"
        >
          <div class="relative h-full w-full">
            <EffectCardCover
              :effect="effect"
              :effect-type="effect.effect_type"
              :alt="effect.title"
            />

            <div
              class="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/70 to-transparent px-3 pb-3 pt-6"
            >
              <p
                class="line-clamp-2 px-3 py-2 text-center text-xs font-semibold leading-snug text-white transition-opacity duration-200 group-hover/card:pointer-events-none group-hover/card:opacity-0"
              >
                {{ effect.title }}
              </p>

              <div
                class="absolute inset-x-3 bottom-3 flex w-[calc(100%-1.5rem)] items-center justify-center gap-1.5 rounded-lg bg-[#8A53FC] px-3 py-2 text-xs font-semibold text-white opacity-0 transition-opacity duration-200 group-hover/card:opacity-100"
              >
                <UIcon name="i-lucide-star" class="size-3.5" />
                Use this template
              </div>
            </div>

            <div
              v-if="effect.effect_type === 'image'"
              class="absolute right-2 top-2 z-10 flex size-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm"
              title="Image effect"
            >
              <UIcon name="i-lucide-image" class="size-4" />
            </div>
          </div>
        </NuxtLink>
      </div>

      <UButton
        class="my-6 w-full justify-center"
        color="primary"
        trailing-icon="i-lucide-arrow-right"
        :to="effectHomeHref"
      >
        Explore all effects
      </UButton>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import EffectCardCover from "@/components/ai-effects/EffectCardCover.vue";
import { normalizeEffectsList } from "@/composables/useAiR2ModelsAndEffects";

const props = defineProps({
  limitVideo: {
    type: Number,
    default: 14,
  },
  limitImage: {
    type: Number,
    default: 7,
  },
  effectHomeHref: {
    type: String,
    default: "/ai/effects/",
  },
  refreshKey: {
    type: String,
    default: "default",
  },
});

const DEFAULT_COVER_ASPECT_RATIO = 3 / 4;
const r2CachedFetch = useR2CachedFetch();

function shuffle(list) {
  const result = [...list];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function prepareCandidates(raw, effectType) {
  return normalizeEffectsList(raw)
    .slice(0, 100)
    .filter((effect) => effect && effect.code)
    .map((effect) => ({
      ...effect,
      effect_type: effect.effect_type || effectType,
    }));
}

function interleaveEffects(videoEffects, imageEffects) {
  const result = [];
  let videoIndex = 0;
  let imageIndex = 0;

  while (videoIndex < videoEffects.length || imageIndex < imageEffects.length) {
    for (let i = 0; i < 2 && videoIndex < videoEffects.length; i += 1) {
      result.push(videoEffects[videoIndex]);
      videoIndex += 1;
    }
    if (imageIndex < imageEffects.length) {
      result.push(imageEffects[imageIndex]);
      imageIndex += 1;
    }
  }

  return result;
}

const recommendations = ref([]);
const pending = ref(import.meta.client);
let fetchRequestId = 0;

async function loadRecommendations() {
  if (!import.meta.client) return;

  const requestId = ++fetchRequestId;
  pending.value = true;

  try {
    const [videoRaw, imageRaw] = await Promise.all([
      r2CachedFetch("/api/r2/ai/effects/video/index.json"),
      r2CachedFetch("/api/r2/ai/effects/image/index.json"),
    ]);
    if (requestId !== fetchRequestId) return;

    const videoEffects = shuffle(prepareCandidates(videoRaw, "video")).slice(
      0,
      props.limitVideo
    );
    const imageEffects = shuffle(prepareCandidates(imageRaw, "image")).slice(
      0,
      props.limitImage
    );

    recommendations.value = interleaveEffects(videoEffects, imageEffects);
  } catch {
    if (requestId !== fetchRequestId) return;
    recommendations.value = [];
  } finally {
    if (requestId === fetchRequestId) pending.value = false;
  }
}

if (import.meta.client) {
  loadRecommendations();
}

watch(
  () => props.refreshKey,
  () => {
    loadRecommendations();
  }
);

function getCoverAspectRatio(effect) {
  const n = Number(effect?.cover_aspect_ratio);
  if (Number.isFinite(n) && n > 0) return n;
  return DEFAULT_COVER_ASPECT_RATIO;
}

function effectCardAspectStyle(effect) {
  return {
    aspectRatio: String(getCoverAspectRatio(effect)),
  };
}

function effectHref(effect) {
  const type = effect?.effect_type === "image" ? "image" : "video";
  return `/ai/effects/${type}/${effect?.code || ""}/`;
}
</script>

<style scoped>
.effect-recommendations__card {
  break-inside: avoid;
  content-visibility: auto;
  contain: layout style paint;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.09) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.6s infinite linear;
}
</style>
